/**
 * Theme preference (design-system.md §2.5): `light` | `dark` | `system`. Stored in
 * localStorage; `system` (the first-load default) leaves `data-theme` unset so the
 * `prefers-color-scheme` block in globals.css applies. Setting the attribute on `<html>`
 * is the ONLY side effect — every token flips off it.
 */
import { create } from 'zustand';

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'argus.theme';

function isPreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

function readInitial(): ThemePreference {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    return isPreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

function persist(theme: ThemePreference): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, theme);
  } catch {
    /* private mode / disabled storage — in-memory only */
  }
}

/** Reflect the preference on `<html data-theme>`; `system` removes the attribute. */
export function applyTheme(theme: ThemePreference): void {
  const root = globalThis.document?.documentElement;
  if (!root) return;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

/** What `system` currently resolves to (used for the toggle icon). */
export function resolveTheme(theme: ThemePreference): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface ThemeState {
  theme: ThemePreference;
  setTheme: (theme: ThemePreference) => void;
}

export const useTheme = create<ThemeState>((set) => ({
  theme: readInitial(),
  setTheme: (theme) => {
    persist(theme);
    applyTheme(theme);
    set({ theme });
  },
}));

/** Call once at boot so the stored preference paints before first render. */
export function initTheme(): void {
  applyTheme(useTheme.getState().theme);
}
